import * as React from 'react'
import moment from 'moment'
import { inject, observer } from 'mobx-react'
import {
  BarChart,
  Bar,
  ResponsiveContainer,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  LineChart,
  Line,
} from 'recharts'
import { API, ISalesByDayOfWeekData, ISalesByHourData } from '@getgreenline/homi-shared'
import { CurrentCompanyStore } from '../../../../../stores/CurrentCompanyStore'
import { ReportFilter } from '../ReportFilter'
import { ReportFilterStore } from '../ReportFilterStore'
import { Skeleton } from 'antd'
import Helmet from 'react-helmet'
import { Track } from '../../../../../Track'

interface IProps {
  currentCompanyStore?: CurrentCompanyStore
  reportFilterStore?: ReportFilterStore
}

interface IState {
  dayOfWeekData: ISalesByDayOfWeekData[]
  hourlyData: ISalesByHourData[]
  isLoading: boolean
}

@inject('currentCompanyStore', 'reportFilterStore')
@observer
export class SalesByDayAndHourReport extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props)
    this.state = {
      dayOfWeekData: [],
      hourlyData: [],
      isLoading: false,
    }
  }

  componentDidMount() {
    this.loadReport()
  }

  loadReport = async () => {
    const { startDate, endDate, selectedLocationIds } = this.props.reportFilterStore!

    this.setState({ isLoading: true })

    const company = this.props.currentCompanyStore!.company!

    const filter = {
      locationIds: selectedLocationIds,
      startDate: moment(startDate).startOf('day').toDate(),
      endDate: moment(endDate).endOf('day').toDate(),
    }

    Track.startTimer('dashboard:reports_dayOfWeekHourly')
    const [{ data: dayOfWeekData }, { data: hourlyData }] = await Promise.all([
      API.getSalesByDayOfWeek(company.id, filter),
      API.getSalesByHour(company.id, filter),
    ])
    Track.track('dashboard:reports_dayOfWeekHourly', {
      numberOfDays: moment(endDate).diff(startDate, 'days'),
    })

    this.setState({ dayOfWeekData, hourlyData, isLoading: false })
  }

  render() {
    const { dayOfWeekData, hourlyData, isLoading } = this.state
    const { endDate } = this.props.reportFilterStore!

    const dayOfWeekDataInDollars = dayOfWeekData.map((data) => ({
      ...data,
      week1: data.week1 / 100,
      week2: data.week2 / 100,
      week3: data.week3 / 100,
    }))

    const hourlyDataInDollar = hourlyData.map((data) => ({
      hour: moment(data.hour, 'HH').format('hh a'),
      week1: data.week1 / 100,
      week2: data.week2 / 100,
      week3: data.week3 / 100,
    }))

    return (
      <>
        <Helmet>
          <title>Reports | Sales By Day and Hour</title>
          <meta name='page-loaded' content='true' />
        </Helmet>

        <ReportFilter maxRangeMonths={12} />

        <div className='alert alert-warning'>
          Showing data for the 3 weeks leading up to {moment(endDate).format('MMM DD, YYYY')}
        </div>

        <hr />

        {isLoading ? (
          <Skeleton active />
        ) : (
          <div className='container p-0'>
            <br />

            <div className='rounded background-white'>
              <div className='p-3'>
                <h4 className='mb-4'>Sales by day of week</h4>
                <ResponsiveContainer height={250}>
                  <BarChart data={dayOfWeekDataInDollars}>
                    <CartesianGrid strokeDasharray='3 3' />
                    <XAxis dataKey='dayOfWeek' />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey='week3' stroke='#8bc34a' fill='#8bc34a' name='three weeks ago' />
                    <Bar dataKey='week2' stroke='#2196f3' fill='#2196f3' name='two weeks ago' />
                    <Bar dataKey='week1' stroke='#f44336' fill='#f44336' name='last week' />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <br />

            <div className='rounded background-white'>
              <div className='p-3'>
                <h4 className='mb-4'>Sales by hour</h4>
                <ResponsiveContainer height={250}>
                  <LineChart height={250} data={hourlyDataInDollar}>
                    <CartesianGrid strokeDasharray='3 3' />
                    <XAxis dataKey='hour' />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line dataKey='week3' stroke='#8bc34a' fill='#8bc34a' name='three weeks ago' />
                    <Line dataKey='week2' stroke='#2196f3' fill='#2196f3' name='two weeks ago' />
                    <Line dataKey='week1' stroke='#f44336' fill='#f44336' name='last week' />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}
      </>
    )
  }
}
